/**
 * page-types/aliases.js - Template Alias Mappings
 * Location: /app/frontend/src/admin/page_builder/page-types/
 * 
 * Maps spec-defined template names (kebab-case) to internal IDs (snake_case).
 * Internal IDs remain canonical; this provides spec compatibility.
 */

/**
 * Spec name (kebab-case) → Internal ID (snake_case)
 */
export const TEMPLATE_ALIASES = {
  // Hero templates
  'hero-overlay': 'hero_overlay',
  'hero-split': 'hero_split',
  'hero-centered': 'hero_minimal',
  'hero-minimal': 'hero_minimal',
  
  // Content/Discovery templates
  'discovery-card-grid': 'featured_grid',
  'discovery-trending-row': 'trending_rail',
  'content-single': 'transparency_footer', // Single column content
  'content-two-column': 'category_spotlight', // Split layout
  
  // Comic/Reader templates
  'comic-episode-list': 'chapter_list',
  'comic-panel-grid': 'chapter_list',
  'comic-vertical-reader': 'vertical_reader_stack',
  'comic-horizontal-reader': 'horizontal_reader',
  'comic-page-reader': 'page_by_page_reader',
  
  // Gallery templates
  'gallery-masonry': 'art_gallery_grid',
  'gallery-uniform': 'art_gallery_grid',
  'gallery-featured': 'featured_grid',
  
  // Profile/Creator templates
  'profile-header': 'creator_spotlight',
  'creator-spotlight': 'creator_spotlight',
  'stats-row': 'leaderboard_strip',
  
  // Community templates
  'community-picks': 'community_picks',
  'community-comments': 'transparency_footer', // Placeholder
  
  // CTA templates
  'cta-banner': 'cta_banner',
  'announcement-banner': 'announcement_banner',
};

/**
 * Internal ID → Spec name (for export/reporting)
 */
export const REVERSE_ALIASES = Object.entries(TEMPLATE_ALIASES).reduce(
  (acc, [specName, internalId]) => {
    // Only keep first mapping (canonical spec name)
    if (!acc[internalId]) {
      acc[internalId] = specName;
    }
    return acc;
  },
  {}
);

/**
 * Resolve a template ID or alias to the internal ID
 * @param {string} idOrAlias - Template ID or spec alias
 * @returns {string} Internal template ID
 */
export function resolveTemplateId(idOrAlias) {
  // Check if it's an alias
  if (TEMPLATE_ALIASES[idOrAlias]) {
    return TEMPLATE_ALIASES[idOrAlias];
  }
  // Return as-is (assume it's already an internal ID)
  return idOrAlias;
}

/**
 * Get the spec-compatible name for an internal ID
 * @param {string} internalId - Internal template ID
 * @returns {string} Spec name or original ID if no alias
 */
export function getSpecName(internalId) {
  return REVERSE_ALIASES[internalId] || internalId;
}

/**
 * Check if a string is a known alias
 * @param {string} str - String to check
 * @returns {boolean}
 */
export function isAlias(str) {
  return str in TEMPLATE_ALIASES;
}

/**
 * Resolve an array of template IDs/aliases to internal IDs
 * @param {string[]} idsOrAliases - Array of IDs or aliases
 * @returns {string[]} Resolved internal IDs
 */
export function resolveTemplateIds(idsOrAliases) {
  return idsOrAliases.map(resolveTemplateId);
}

export default {
  TEMPLATE_ALIASES,
  REVERSE_ALIASES,
  resolveTemplateId,
  getSpecName,
  isAlias,
  resolveTemplateIds,
};
